export const DEBUG_ASSETHASH = null; // <asset_id of asset to debug> or null
export const DEBUG_HASHASSETS = false;
export const DEBUG_SETMETADATA = false;
export const DEBUG_HASHMETADATA = false;
export const DEBUG_HASHPROPERTIES = false;
export const DEBUG_SETPROPERTIES = false;
export const NUM_COLOR_BINS = 7;
export const CM_PER_INCH = 2.54;
export const ROUND_COORDS_TO = 6;
export const CUSTOM_ATTRIB_NAME_MAX_LEN = 1000;
export const CUSTOM_ATTRIB_VALUE_MAX_LEN = 10000;

export const secondsIn = {
    // Converts other unit of time into seconds
    minutes: (mins) => mins * 60,
    hours: (hours) => hours * 3600,
    days: (days) => days * 86400,
    weeks: (weeks) => weeks * 604800,
    months: (months) => months * 2592000,
    years: (years) => years * 31536000,
};

export const millisecondsIn = {
    // Converts other unit of time into milliseconds
    seconds: (secs) => secs * 1000,
    minutes: (mins) => secondsIn.minutes(mins) * 1000,
    hours: (hours) => secondsIn.hours(hours) * 1000,
    days: (days) => secondsIn.days(days) * 1000,
    weeks: (weeks) => secondsIn.weeks(weeks) * 1000,
    months: (months) => secondsIn.months(months) * 1000,
    years: (years) => secondsIn.years(years) * 1000,
};

export const ProjectType = {
    'Tree': 'tree',
    'Irrigation': 'irrigation',
    'Lab': 'lab',
    'Asset': 'asset',
};

export const PROJECT_TYPES = Object.values(ProjectType);

export const AssetType = {
    // This must match the class in ./backend/api/constants.py AssetType
    'Tree': 'tree',

    'SprinklerHead': 'sprinkler_head',
    'WaterLine': 'water_line',
    'Valve': 'valve',
    'Controller': 'controller',
    'WaterSource': 'water_source',

    'PlantSample': 'plant_sample',
    'TurfSample': 'turf_sample',

    'Point': 'point',
    'Line': 'line',
    'Polygon': 'polygon',

    'Any': 'any',
    'Pavement': 'pavement',
    'Mulch': 'mulch',

    'EstimatorPolygon': 'estimator_polygon',
    'EstimatorLine': 'estimator_line',
};

export const ASSET_TYPES = Object.values(AssetType);

export const ASSET_TYPES_EXCLUDED_FROM_AC = [
    AssetType.Any,
];

export const ASSET_TYPES_IN_PROJECT = {
    // This must match the dict in ./backend/api/constants.py ASSET_TYPES_IN_PROJECT
    [ProjectType.Tree]: [
        AssetType.Tree,
    ],
    [ProjectType.Irrigation]: [
        AssetType.SprinklerHead,
        AssetType.WaterLine,
        AssetType.Valve,
        AssetType.Controller,
        AssetType.WaterSource,
    ],
    [ProjectType.Lab]: [
        AssetType.PlantSample,
        AssetType.TurfSample,
    ],
    [ProjectType.Asset]: ASSET_TYPES.filter((asset_type) => !ASSET_TYPES_EXCLUDED_FROM_AC.includes(asset_type)),
};

export const ASSET_NAME = {
    [AssetType.Tree]: 'Tree',
    [AssetType.SprinklerHead]: 'Sprinkler',
    [AssetType.WaterLine]: 'Water Line',
    [AssetType.Valve]: 'Valve',
    [AssetType.Controller]: 'Controller',
    [AssetType.WaterSource]: 'Water Source',
    [AssetType.Point]: 'Point',
    [AssetType.Line]: 'Line',
    [AssetType.Polygon]: 'Polygon',
    [AssetType.PlantSample]: 'Plant Sample',
    [AssetType.TurfSample]: 'Turf Sample',
    [AssetType.Pavement]: 'Pavement',
    [AssetType.Mulch]: 'Mulch',
    [AssetType.EstimatorPolygon]: 'Area Estimator Polygon',
    [AssetType.EstimatorLine]: 'Area Estimator Line',
};

export const FeatureType = {
    'Point': 'Point',
    'Line': 'LineString',
    'Polygon': 'Polygon',
    'Circle': 'Circle',
};

export const ASSET_TYPE_FEATURE_TYPE = {
    [AssetType.WaterLine]: FeatureType.Line,
    [AssetType.Line]: FeatureType.Line,
    [AssetType.Polygon]: FeatureType.Polygon,
    [AssetType.Pavement]: FeatureType.Polygon,
    [AssetType.Mulch]: FeatureType.Polygon,
    [AssetType.EstimatorPolygon]: FeatureType.Polygon,
    [AssetType.EstimatorLine]: FeatureType.Line,
};

export const AttributeType = {
    'String': 'StringAttribute',
    'BigString': 'BigStringAttribute',
    'NearestAddress': 'NearestAddressAttribute',
    'Id': 'IdAttribute',
    'Image': 'ImageAttribute',
    'Number': 'NumberAttribute',
    'SliderNumber': 'SliderNumberAttribute',
    'Integer': 'IntegerAttribute',
    'SliderInteger': 'SliderIntegerAttribute',
    'SingleOption': 'SingleOptionAttribute',
    'MultiOption': 'MultiOptionAttribute',
    'DropdownOption': 'DropdownOptionAttribute',
    'DoubleDropdown': 'DoubleDropdownAttribute',
    'Timestamp': 'TimestampAttribute',
    'Date': 'DateAttribute',
    'SearchDropdown': 'SearchDropdownAttribute',
    'KeyedSearchDropdown': 'KeyedSearchDropdownAttribute',
    'KeyedSingleOption': 'KeyedSingleOptionAttribute',
    'KeyedDropdownOption': 'KeyedDropdownOptionAttribute',
    'DoubleNumber': 'DoubleNumberAttribute',
    'Proportion': 'ProportionAttribute',
    'ButtonSingleOption': 'ButtonSingleOptionAttribute',
    'KeyedButtonSingleOption': 'KeyedButtonSingleOptionAttribute',
    'ButtonMultiOption': 'ButtonMultiOptionAttribute',
    'ExpandingButtonMultiOption': 'ExpandingButtonMultiOptionAttribute',
    'SpeciesSearchDropdown': 'SpeciesSearchDropdownAttribute',
    'KeyedMultiOption': 'KeyedMultiOptionAttribute',
    'KeyedButtonMultiOption': 'KeyedButtonMultiOptionAttribute',
    'PolygonCoordinates': 'PolygonCoordinatesAttribute',
    'Dbh': 'DbhAttribute',
    'CustomSpecies': 'CustomSpeciesAttribute',
};

export const DEFAULT_COLOR_FIELD_VALUES = {
    ['default:'+ASSET_NAME[AssetType.Tree]]: '#00ff00',
    ['default:'+ASSET_NAME[AssetType.SprinklerHead]]: '#00ff00',
    ['default:'+ASSET_NAME[AssetType.WaterLine]]: '#00ff00',
    ['symbol:default:'+ASSET_NAME[AssetType.WaterLine]]: "solid_line",
    ['default:'+ASSET_NAME[AssetType.Valve]]: '#00ff00',
    ['default:'+ASSET_NAME[AssetType.Controller]]: '#00ff00',
    ['default:'+ASSET_NAME[AssetType.WaterSource]]: '#00ff00',
    ['default:'+ASSET_NAME[AssetType.PlantSample]]: '#00ff00',
    ['default:'+ASSET_NAME[AssetType.TurfSample]]: '#ffdb13',
    ['default:'+ASSET_NAME[AssetType.Point]]: '#00ff00',
    ['default:'+ASSET_NAME[AssetType.Line]]: '#00ff00',
    ['default:'+ASSET_NAME[AssetType.Polygon]]: '#00ff00',
    ['default:'+ASSET_NAME[AssetType.Pavement]]: '#00ff00',
    ['default:'+ASSET_NAME[AssetType.Mulch]]: '#00ff00',
    ['default:'+ASSET_NAME[AssetType.EstimatorPolygon]]: '#ebebeb',
    ['default:'+ASSET_NAME[AssetType.EstimatorLine]]: '#ebebeb',
    "dbh:minimum": "#fbfe00",
    "dbh:maximum": "#0013ff",
    "condition:Excellent": "#22f815",
    "condition:Good": "#2ec27e",
    "condition:Fair": "#f5c211",
    "condition:Poor": "#ff7800",
    "condition:Critical": "#fb2309",
    "condition:Dead": "#000000",
    "condition:Removed": "#5e5c64",
    "tree_type:Hardwood": "#ff7800",
    "tree_type:Softwood": "#8ff0a4",
    "tree_type:Other": "#f9f06b",
    "revisit:Yes": "#57e389",
    "revisit:No": "#ed333b",
    "priority:High": "#ed333b",
    "priority:Medium": "#ffa348",
    "priority:Low": "#57e389",
    "structure:Excellent": "#22f815",
    "structure:Very Good": "#12d806",
    "structure:Good": "#25900d",
    "structure:Fair": "#f6d32d",
    "structure:Poor": "#ff7800",
    "irrigation_needs:High": "#e01b24",
    "irrigation_needs:Medium": "#ffa348",
    "irrigation_needs:Low": "#57e389",
    "primary_maintenance:Building Clearance": "#ffbe6f",
    "primary_maintenance:Cabling": "#99c1f1",
    "primary_maintenance:Bracing": "#62a0ea",
    "primary_maintenance:Clearance": "#000000",
    "primary_maintenance:Crown Reduce": "#ffa348",
    "primary_maintenance:Crown Thin": "#ff7800",
    "primary_maintenance:Dormant Prune": "#000000",
    "primary_maintenance:Palm Prune": "#e66100",
    "primary_maintenance:HMI Tree Pruning": "#000000",
    "primary_maintenance:Plant Tree - Small": "#8ff0a4",
    "primary_maintenance:Plant Tree - Medium": "#57e389",
    "primary_maintenance:Plant Tree - Large": "#33d17a",
    "primary_maintenance:Raise Canopy": "#c64600",
    "primary_maintenance:Removal": "#ed333b",
    "primary_maintenance:Removal - CRITICAL": "#e01b24",
    "primary_maintenance:Remove Deadwood": "#77767b",
    "primary_maintenance:Storm Damage": "#000000",
    "primary_maintenance:Stump Removal": "#000000",
    "primary_maintenance:Tree Clean": "#3584e4",
    "primary_maintenance:Young Tree Train": "#1c71d8",
    "primary_maintenance:Other - See Notes": "#a1aef1",
    "symbol:condition:Removed": "diagonal_cross",
    "symbol:tree_type:Hardwood": "five_pt_star",
    "symbol:tree_type:Softwood": "triangle",
    "symbol:primary_maintenance:Removal": "diagonal_cross",
    "symbol:primary_maintenance:Stump Removal": "diagonal_cross",
    "symbol:primary_maintenance:Plant Tree - Large": "cross",
    "symbol:primary_maintenance:Plant Tree - Small": "cross",
    "symbol:primary_maintenance:Removal - CRITICAL": "diagonal_cross",
    "symbol:primary_maintenance:Plant Tree - Medium": "cross",
    "estimator_type:Boundary": "#ff0000",
    "estimator_type:Soft Edging": "#fda800",
    "estimator_type:Hard Edging": "#fffc01",
    "estimator_type:Sidewalk": "#ff2e17",
    "estimator_type:Roads": "#0074fe",
    "estimator_type:Driveway": "#00ffff",
    "estimator_type:Beds": "#ab6766",
    "estimator_type:Turf": "#94f067",
    "estimator_type:Turf Low Maint": "#ffe5b2",
    "estimator_type:Turf Other": "#008040",
    "estimator_type:Parking": "#8000ff",
    "estimator_type:Common Area": "#ff80ff",
    "estimator_type:Other Hard Surface": "#ff0080",
};

export const MapDrawingMethod = {
    'ClickPoints': 'click_points',
    'ColorSelect': 'color_select',
    'PaintBrush': 'paint_brush',
    'PaintBrushPerimeter': 'paint_brush_perimeter',
    'ThickLine': 'thick_line',
    'FloodFill': 'flood_fill',
};

export const SITE_SETTINGS_DEFAULTS = {
    'units': 'English',
    'auto_populate': 'Yes',

    'trunk_measurement': 'Diameter',
    'pinned_tree_species': [],
    'custom_tree_species': [],

    'preferred_theme': 'system',
    'create_new_assets_from_data_page': 'No',
    'offline_mode': 'No',
    'map_context_menu': 'Custom',
    'pick_drawing_method_on_map': 'No',
    'show_dashboard_help_info': 'Yes',
    'verbose_project_buttons': 'Yes',
    'default_used_attributes': 'All',
    'asset_system_category': 'Hide',
    'projects_per_page': 15,
    'assets_per_page': 15,
};

export const SITE_SETTINGS = [
    {
        'category_name': 'Display',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Units',
        'internal_name': 'units',
        'required': true,
        'repeatable': false,
        'options': ['English', 'Metric'],
    },
    {
        'category_name': 'Display',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Auto Load Last Feature Collected',
        'internal_name': 'auto_populate',
        'required': true,
        'repeatable': false,
        'options': ['Yes', 'No'],
    },

    {
        'category_name': 'Trees',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Trunk Measurement',
        'internal_name': 'trunk_measurement',
        'required': true,
        'repeatable': false,
        'options': ['Diameter', 'Circumference'],
    },
    {
        'category_name': 'Trees',
        'attribute_type': AttributeType.SpeciesSearchDropdown,
        'name': 'Pinned Tree Species',
        'internal_name': 'pinned_tree_species',
        'required': false,
        'repeatable': true,
        'options': {},
    },
    {
        'category_name': 'Trees',
        'attribute_type': AttributeType.CustomSpecies,
        'name': 'Custom Tree Species',
        'internal_name': 'custom_tree_species',
        'required': false,
        'repeatable': true,
    },

    {
        'category_name': 'Power User',
        'attribute_type': AttributeType.KeyedButtonSingleOption,
        'name': 'Preferred Theme',
        'internal_name': 'preferred_theme',
        'required': true,
        'options': {
            'system': 'System',
            'dark': 'Dark',
            'light': 'Light',
        },
    },
    {
        'category_name': 'Power User',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Create New Assets from Data Page',
        'subname': '(Requires manual input of coordinates)',
        'internal_name': 'create_new_assets_from_data_page',
        'required': true,
        'repeatable': false,
        'options': ['Yes', 'No'],
    },
    {
        'category_name': 'Power User',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Offline Mode - Open online beforehand',
        'subname': '(For remote areas with no service)',
        'internal_name': 'offline_mode',
        'required': true,
        'repeatable': false,
        'options': ['Yes', 'No'],
    },
    {
        'category_name': 'Power User',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Map Context Menu',
        'internal_name': 'map_context_menu',
        'required': true,
        'repeatable': false,
        'options': ['Default', 'Custom'],
    },
    {
        'category_name': 'Power User',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Pick Drawing Method on Map',
        'internal_name': 'pick_drawing_method_on_map',
        'required': true,
        'options': [ 'Yes', 'No' ],
    },
    {
        'category_name': 'Power User',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Show Dashboard Help Info',
        'internal_name': 'show_dashboard_help_info',
        'required': true,
        'repeatable': false,
        'options': ['Yes', 'No'],
    },
    {
    'category_name': 'Power User',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Verbose Project Buttons',
        'internal_name': 'verbose_project_buttons',
        'required': true,
        'repeatable': false,
        'options': ['Yes', 'No'],
    },
    {
    'category_name': 'Power User',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Default Used Attributes',
        'internal_name': 'default_used_attributes',
        'required': true,
        'repeatable': false,
        'options': ['All', 'Only Required'],
    },
    {
    'category_name': 'Power User',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Asset System Category',
        'internal_name': 'asset_system_category',
        'required': true,
        'repeatable': false,
        'options': ['Hide', 'Show'],
    },
    {
        'category_name': 'Power User',
        'attribute_type': AttributeType.Integer,
        'name': 'Projects displayed per page',
        'internal_name': 'projects_per_page',
        'required': true,
        'repeatable': false,
        'minVal': 1,
        'maxVal': 10000,
    },
    {
        'category_name': 'Power User',
        'attribute_type': AttributeType.Integer,
        'name': 'Assets displayed per page',
        'internal_name': 'assets_per_page',
        'required': true,
        'repeatable': false,
        'minVal': 1,
        'maxVal': 10000,
    },
];

export const MAP_SETTINGS_DEFAULTS = {
    'map_imagery': 'Google',
    'clustering': 'Off',
    'truncate_label_after': '500',
    'wrap_label_after': '500',
    'cluster_distance': '10',
    'show_polygon_edges': 'Yes',
    'polygon_fill_opacity': 50,
    'marker_size': '10',
    'marker_outline_width': '2',
    'label': [],
    'label_position': 'Top',
    'label_offset': '10',
    'marker_color': 'Default',
    'text_size': '12',
    'text_highlight_width': '3',
    'invert_text_color': 'No',
    'count_legend_items': 'Yes',
    'limit_legend_to_used_options': 'Yes',
    'legend_item_limit': 15,
    'autocolor_seed': '',
    'numeric_marker_color': 'Static Range',
    'include_field_name_in_label': 'No',
    'multi_label_separator': 'Newline',
    'map_imagery_opacity': 100,
    'map_imagery_background': 'White',
    'polygon_drawing_method': MapDrawingMethod.ClickPoints,
    'multi_stage_drawing': 'Disabled',
    'instantly_create_blank_assets': 'No',
    'search_location_with_coordinates': 'No',
};

export const MAP_SETTINGS = [
    {
        'category_name': 'Basic',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Map Imagery',
        'internal_name': 'map_imagery',
        'required': true,
        'repeatable': false,
        'options': [],
    },
    {
        'category_name': 'Basic',
        'attribute_type': AttributeType.KeyedDropdownOption,
        'name': 'Marker Label',
        'internal_name': 'label',
        'required': false,
        'repeatable': true,
        'options': {},
    },
    {
        'category_name': 'Basic',
        'attribute_type': AttributeType.KeyedDropdownOption,
        'name': 'Marker Color',
        'internal_name': 'marker_color',
        'required': true,
        'repeatable': false,
        'options': ['Default'],
    },

    {
        'category_name': 'Markers, Advanced',
        'attribute_type': AttributeType.SliderInteger,
        'name': 'Marker Size',
        'internal_name': 'marker_size',
        'required': true,
        'repeatable': false,
        'minVal': 0,
        'maxVal': 30,
        'step': 1,
        'unit': 'pixels',
    },
    {
        'category_name': 'Markers, Advanced',
        'attribute_type': AttributeType.SliderInteger,
        'name': 'Marker Outline Width',
        'internal_name': 'marker_outline_width',
        'required': true,
        'repeatable': false,
        'minVal': 0,
        'maxVal': 10,
        'unit': 'pixels',
    },
    {
        'category_name': 'Markers, Advanced',
        'attribute_type': AttributeType.String,
        'name': 'Autocolor Seed',
        'internal_name': 'autocolor_seed',
        'required': false,
        'repeatable': false,
        'minLen': 0,
        'maxLen': 20,
        'unit': 'random characters',
    },
    {
        'category_name': 'Markers, Advanced',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Numeric Marker Color',
        'internal_name': 'numeric_marker_color',
        'required': true,
        'repeatable': false,
        'options': ['Static Range', 'Asset Bounds'],
    },
    {
        'category_name': 'Markers, Advanced',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Clustering',
        'internal_name': 'clustering',
        'required': true,
        'repeatable': false,
        'options': ['On', 'Off'],
    },
    {
        'category_name': 'Markers, Advanced',
        'attribute_type': AttributeType.Integer,
        'name': 'Cluster Distance',
        'internal_name': 'cluster_distance',
        'required': true,
        'repeatable': false,
        'minVal': 0,
        'maxVal': 500,
        'unit': 'pixels',
    },
    {
        'category_name': 'Markers, Advanced',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Show Polygon Edges',
        'internal_name': 'show_polygon_edges',
        'required': true,
        'repeatable': false,
        'options': ['Yes', 'No'],
    },
    {
        'category_name': 'Markers, Advanced',
        'attribute_type': AttributeType.SliderInteger,
        'name': 'Polygon Fill Opacity',
        'internal_name': 'polygon_fill_opacity',
        'required': true,
        'repeatable': false,
        'minVal': 0,
        'maxVal': 100,
        'step': 5,
        'unit': 'percentage',
    },


    {
        'category_name': 'Labels, Advanced',
        'attribute_type': AttributeType.SliderInteger,
        'name': 'Marker Label Offset',
        'internal_name': 'label_offset',
        'required': true,
        'repeatable': false,
        'minVal': 0,
        'maxVal': 30,
        'step': 1,
        'unit': 'pixels',
    },
    {
        'category_name': 'Labels, Advanced',
        'attribute_type': AttributeType.SingleOption,
        'name': 'Marker Label Position',
        'internal_name': 'label_position',
        'required': true,
        'repeatable': false,
        'options': ['Top','Bottom','Left','Right','Top and Bottom','Left and Right'],
    },
    {
        'category_name': 'Labels, Advanced',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Include Field Name in Label',
        'internal_name': 'include_field_name_in_label',
        'required': true,
        'repeatable': false,
        'options': ['Yes', 'No'],
    },
    {
        'category_name': 'Labels, Advanced',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Multi Label Separator',
        'internal_name': 'multi_label_separator',
        'required': true,
        'repeatable': false,
        'options': ['Newline', 'Space', 'Comma'],
    },
    {
        'category_name': 'Labels, Advanced',
        'attribute_type': AttributeType.SliderInteger,
        'name': 'Text Size',
        'internal_name': 'text_size',
        'required': true,
        'repeatable': false,
        'minVal': 0,
        'maxVal': 30,
        'unit': 'pixels',
    },
    {
        'category_name': 'Labels, Advanced',
        'attribute_type': AttributeType.SliderInteger,
        'name': 'Text Highlight Width',
        'internal_name': 'text_highlight_width',
        'required': true,
        'repeatable': false,
        'minVal': 0,
        'maxVal': 30,
        'unit': 'pixels',
    },
    {
        'category_name': 'Labels, Advanced',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Invert Text Color',
        'internal_name': 'invert_text_color',
        'required': true,
        'repeatable': false,
        'options': ['Yes', 'No'],
    },
    {
        'category_name': 'Labels, Advanced',
        'attribute_type': AttributeType.Integer,
        'name': 'Truncate Label After',
        'internal_name': 'truncate_label_after',
        'required': true,
        'repeatable': false,
        'minVal': 0,
        'maxVal': 500,
        'unit': 'characters',
    },
    {
        'category_name': 'Labels, Advanced',
        'attribute_type': AttributeType.Integer,
        'name': 'Wrap Label After',
        'internal_name': 'wrap_label_after',
        'required': true,
        'repeatable': false,
        'minVal': 0,
        'maxVal': 500,
        'unit': 'characters',
    },

    {
        'category_name': 'Other Advanced',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Count Legend Items',
        'internal_name': 'count_legend_items',
        'required': true,
        'repeatable': false,
        'options': ['Yes', 'No'],
    },
    {
        'category_name': 'Other Advanced',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Limit Legend to Used Options',
        'internal_name': 'limit_legend_to_used_options',
        'required': true,
        'repeatable': false,
        'options': ['Yes', 'No'],
    },
    {
        'category_name': 'Other Advanced',
        'attribute_type': AttributeType.Integer,
        'name': 'Legend Item Limit (autocolor fields)',
        'internal_name': 'legend_item_limit',
        'required': true,
        'repeatable': false,
        'minVal': 1,
        'maxVal': 100,
        'unit': 'items',
    },
    {
        'category_name': 'Other Advanced',
        'attribute_type': AttributeType.SliderInteger,
        'name': 'Map Imagery Opacity',
        'internal_name': 'map_imagery_opacity',
        'required': true,
        'repeatable': false,
        'minVal': 0,
        'maxVal': 100,
        'step': 5,
        'unit': 'percentage',
    },
    {
        'category_name': 'Other Advanced',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Map Imagery Background',
        'internal_name': 'map_imagery_background',
        'required': true,
        'repeatable': false,
        'options': ['White', 'Black'],
    },
    {
        'category_name': 'Other Advanced',
        'attribute_type': AttributeType.KeyedDropdownOption,
        'name': 'Polygon Drawing Method',
        'internal_name': 'polygon_drawing_method',
        'required': true,
        'repeatable': false,
        'options': {
            [MapDrawingMethod.ClickPoints]: 'Click Points',
            [MapDrawingMethod.ColorSelect]: 'Fuzzy Color Select',
            [MapDrawingMethod.PaintBrush]: 'Paint Brush',
            [MapDrawingMethod.PaintBrushPerimeter]: 'Paint Brush Perimeter',
        },
    },
    {
        'category_name': 'Other Advanced',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Multi-Stage Drawing',
        'internal_name': 'multi_stage_drawing',
        'required': true,
        'options': [ 'Enabled', 'Disabled'],
    },
    {
        'category_name': 'Other Advanced',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Instantly Create Blank Assets',
        'subname': 'Skip filling asset form to save time',
        'internal_name': 'instantly_create_blank_assets',
        'required': true,
        'options': [ 'Yes', 'No'],
    },
    {
        'category_name': 'Other Advanced',
        'attribute_type': AttributeType.ButtonSingleOption,
        'name': 'Search Location with Coordinates',
        'internal_name': 'search_location_with_coordinates',
        'required': true,
        'options': [ 'Yes', 'No'],
    },

];

export const Separator = {
    'Newline': '\n',
    'Space': ' ',
    'Comma': ', ',
};

export const MAP_EXPORT_SETTINGS_DEFAULTS = {
    'image_aspect_ratio': '1',
    'image_dimensions_based_on': '1',
    'image_resolution': '2',
    'title': 'Unnamed Project',
    'subtitles': ['Kent, OH'],
};

export const MAP_EXPORT_SETTINGS = [
    {
        'category_name': '',
        'attribute_type': AttributeType.KeyedButtonSingleOption,
        'name': 'Image Aspect Ratio',
        'internal_name': 'image_aspect_ratio',
        'required': true,
        'repeatable': false,
        'options': {
            1: 'Portrait (8.5 x 11)',
            2: 'Landscape (11 x 8.5)',
            3: 'Square (11 x 11)',
            4: 'Portrait (11 x 17)',
            5: 'Landscape (17 x 11)',
        },
    },
    {
        'category_name': '',
        'attribute_type': AttributeType.KeyedButtonSingleOption,
        'name': 'Image Dimensions Based On',
        'internal_name': 'image_dimensions_based_on',
        'required': true,
        'repeatable': false,
        'options': {
            1: 'Browser Width',
            2: 'Browser Height',
        },
    },
    {
        'category_name': '',
        'attribute_type': AttributeType.KeyedButtonSingleOption,
        'name': 'Image Resolution',
        'internal_name': 'image_resolution',
        'required': true,
        'repeatable': false,
        'options': {
            1: '150 DPI',
            2: '300 DPI',
            3: '600 DPI',
        },
    },
    {
        'category_name': '',
        'attribute_type': AttributeType.String,
        'name': 'Title',
        'internal_name': 'title',
        'required': false,
        'repeatable': false,
        'minLen': 0,
        'maxLen': 100,
        'selectOnClick': true,
    },
    {
        'category_name': '',
        'attribute_type': AttributeType.String,
        'name': 'Subtitles',
        'internal_name': 'subtitles',
        'required': false,
        'repeatable': true,
        'minLen': 0,
        'maxLen': 40,
        'selectOnClick': false,
    },
];

export const PROJECT_FEATURE_NAME = {
    [ProjectType.Tree]: 'Tree',
    [ProjectType.Irrigation]: 'Irrigation Feature',
    [ProjectType.Lab]: 'Sample',
    [ProjectType.Asset]: 'Asset',
};

export const DEFAULT_MAP_CENTER = [-98.5795, 39.868]; // Center of US

// Populate default values (if not autofilled from last asset)
export const DEFAULT_ASSET_VALUES_AFTER_AUTOFILL = {
    [ProjectType.Tree]: {
        'sun_exposure': '3',
        'condition': 'Good',
    },
    [ProjectType.Irrigation]: {},
    [ProjectType.Lab]: {},
    [ProjectType.Asset]: {},
};

export const ATTRIBUTE_INTERNAL_NAMES_TO_NOT_AUTOPOPULATE = {
    [ProjectType.Tree]: ['photo', 'notes'],
    [ProjectType.Irrigation]: [],
    [ProjectType.Lab]: [],
    [ProjectType.Asset]: [],
};

export const ExportField = {
    'AssetId': 'export_field_asset_id',
    'Longitude': 'export_field_longitude',
    'Latitude': 'export_field_latitude',
    'CoordinatesLngLat': 'export_field_coordinates_lng_lat',
    'CoordinatesLatLng': 'export_field_coordinates_lat_lng',
    'TimestampCreated': 'export_field_timestamp_created',
    'DatetimeCreated': 'export_field_datetime_created',
    'TimestampModified': 'export_field_timestamp_modified',
    'DatetimeModified': 'export_field_datetime_modified',
    'SpeciesNameScientific': 'export_field_species_name_scientific',
    'SpeciesNameCommon': 'export_field_species_name_common',
    'SpeciesNameBoth': 'export_field_species',
    'SpeciesType': 'export_field_species_type',
    'SpeciesLeafType': 'export_field_species_leaf_type',
    'SpeciesPercentLeafType': 'export_field_species_percent_leaf_type',
    'SpeciesCode': 'export_field_species_code',
    'SpeciesGenusScientific': 'export_field_species_genus_scientific',
    'SpeciesGenusCommon': 'export_field_species_genus_common',
    'Dbh': 'export_field_dbh',
    'DbhIn': 'export_field_dbh_in',
    'DbhCm': 'export_field_dbh_cm',
    'SunExposureCode': 'export_field_sun_exposure_code',
    'SunExposure': 'export_field_sun_exposure',
    'Length': 'export_field_length',
    'Area': 'export_field_area',
    'Perimeter': 'export_field_perimeter',
    'LengthFt': 'export_field_length_ft',
    'LengthM': 'export_field_length_m',
    'AreaSqM': 'export_field_area_sq_m',
    'AreaSqFt': 'export_field_area_sq_ft',
    'PerimeterFt': 'export_field_perimeter_ft',
    'PerimeterM': 'export_field_perimeter_m',
    'Volume': 'export_field_volume',
    'VolumeCuFt': 'export_field_volume_cu_ft',
    'VolumeCuM': 'export_field_volume_cu_m',
    'NumPhotos': 'export_field_num_photos',
};

const generic_polygon_export_fields = [
    ExportField.Area,
    ExportField.AreaSqFt,
    ExportField.AreaSqM,
    ExportField.Perimeter,
    ExportField.PerimeterFt,
    ExportField.PerimeterM,
];

const generic_line_export_fields = [
    ExportField.Length,
    ExportField.LengthFt,
    ExportField.LengthM,
];

export const DERIVED_EXPORT_FIELDS = {
    [AssetType.Any]: [
        ExportField.AssetId,
        ExportField.TimestampCreated,
        ExportField.DatetimeCreated,
        ExportField.TimestampModified,
        ExportField.DatetimeModified,
        ExportField.NumPhotos,
    ],
    [AssetType.Tree]: [
        ExportField.SpeciesNameScientific,
        ExportField.SpeciesNameCommon,
        ExportField.SpeciesNameBoth,
        ExportField.SpeciesType,
        ExportField.SpeciesLeafType,
        ExportField.SpeciesPercentLeafType,
        ExportField.SpeciesCode,
        ExportField.SpeciesGenusScientific,
        ExportField.SpeciesGenusCommon,
        ExportField.Dbh,
        ExportField.DbhIn,
        ExportField.DbhCm,
        ExportField.SunExposureCode,
        ExportField.SunExposure,
    ],
    [AssetType.WaterLine]: [
        ...generic_line_export_fields,
    ],
    [AssetType.Line]: [
        ...generic_line_export_fields,
    ],
    [AssetType.Polygon]: [
        ...generic_polygon_export_fields,
    ],
    [AssetType.Pavement]: [
        ...generic_polygon_export_fields,
    ],
    [AssetType.Mulch]: [
        ...generic_polygon_export_fields,
        ExportField.Volume,
        ExportField.VolumeCuFt,
        ExportField.VolumeCuM,
    ],
    [AssetType.EstimatorPolygon]: [
        ...generic_polygon_export_fields,
    ],
    [AssetType.EstimatorLine]: [
        ...generic_line_export_fields,
    ],
};

export const UNEXPORTED_INTERNAL_NAMES = {
    [AssetType.Any]: [
        'coordinates',
        'time_created',
        'time_modified',
        'photo',
    ],
    [AssetType.Tree]: [
        'species',
        'dbh',
        'sun_exposure',
    ],
};

export const EXPORT_FIELD_PRETTY_NAMES = {
    [ExportField.AssetId]: 'Asset ID',
    [ExportField.Longitude]: 'Longitude',
    [ExportField.Latitude]: 'Latitude',
    [ExportField.CoordinatesLngLat]: 'Coordinates (lng, lat)',
    [ExportField.CoordinatesLatLng]: 'Coordinates (lat, lng)',
    [ExportField.TimestampCreated]: 'Timestamp Created',
    [ExportField.DatetimeCreated]: 'Datetime Created',
    [ExportField.TimestampModified]: 'Timestamp Modified',
    [ExportField.DatetimeModified]: 'Datetime Modified',
    [ExportField.SpeciesNameScientific]: 'Species Name Scientific',
    [ExportField.SpeciesNameCommon]: 'Species Name Common',
    [ExportField.SpeciesNameBoth]: 'Species',
    [ExportField.SpeciesType]: 'Species Type',
    [ExportField.SpeciesLeafType]: 'Leaf Type',
    [ExportField.SpeciesPercentLeafType]: 'Tree Type',
    [ExportField.SpeciesCode]: 'Species Code',
    [ExportField.SpeciesGenusScientific]: 'Tree Genus Scientific',
    [ExportField.SpeciesGenusCommon]: 'Tree Genus Common',
    [ExportField.Dbh]: 'DBH',
    [ExportField.DbhIn]: 'DBH (inches)',
    [ExportField.DbhCm]: 'DBH (centimeters)',
    [ExportField.SunExposureCode]: 'Sun Exposure Code',
    [ExportField.SunExposure]: 'Sun Exposure',
    'leaf_type': 'Leaf Type (legacy)',
    [ExportField.Length]: 'Length',
    [ExportField.LengthFt]: 'Length (ft)',
    [ExportField.LengthM]: 'Length (m)',
    [ExportField.Area]: 'Area',
    [ExportField.AreaSqFt]: 'Area (sq ft)',
    [ExportField.AreaSqM]: 'Area (sq m)',
    [ExportField.Perimeter]: 'Perimeter',
    [ExportField.PerimeterFt]: 'Perimeter (ft)',
    [ExportField.PerimeterM]: 'Perimeter (m)',
    [ExportField.Volume]: 'Volume',
    [ExportField.VolumeCuFt]: 'Volume (cu ft)',
    [ExportField.VolumeCuM]: 'Volume (cu m)',
    [ExportField.NumPhotos]: 'Number of Photos',
};

export const EXPORT_FIELD_ORDER = {
    // Dictionary of <attribute_internal_name> to <array of derived_field_names which come after it>
    'coordinates': [
        ExportField.Longitude,
        ExportField.Latitude,
        ExportField.CoordinatesLngLat,
        ExportField.CoordinatesLatLng,
        ExportField.Length,
        ExportField.LengthFt,
        ExportField.LengthM,
        ExportField.Area,
        ExportField.AreaSqFt,
        ExportField.AreaSqM,
        ExportField.Perimeter,
        ExportField.PerimeterFt,
        ExportField.PerimeterM,
        ExportField.Volume,
        ExportField.VolumeCuFt,
        ExportField.VolumeCuM,
    ],
    'time_created': [
        ExportField.TimestampCreated,
        ExportField.DatetimeCreated,
    ],
    'time_modified': [
        ExportField.TimestampModified,
        ExportField.DatetimeModified,
    ],
    'species': [
        ExportField.SpeciesCode,
        ExportField.SpeciesNameCommon,
        ExportField.SpeciesNameScientific,
        ExportField.SpeciesNameBoth,
        ExportField.SpeciesType,
        ExportField.SpeciesLeafType,
        ExportField.SpeciesPercentLeafType,
        ExportField.SpeciesGenusScientific,
        ExportField.SpeciesGenusCommon,
    ],
    'dbh': [
        ExportField.Dbh,
        ExportField.DbhIn,
        ExportField.DbhCm,
    ],
    'sun_exposure': [
        ExportField.SunExposure,
        ExportField.SunExposureCode,
    ],
    'photo': [
        ExportField.NumPhotos,
    ],
};

export const EXPORT_FIELD_DERIVED_FROM = Object.fromEntries(
    Object.entries(EXPORT_FIELD_ORDER).flatMap(([key, values]) =>
        values.map(value => [value, key])
    )
);

export const EXPORT_FIELDS_TO_AUTOCOLOR = [
    ExportField.SpeciesNameCommon,
    ExportField.SpeciesNameScientific,
    ExportField.SpeciesLeafType,
    ExportField.SpeciesPercentLeafType,
    ExportField.SpeciesGenusScientific,
    ExportField.SpeciesGenusCommon,
];

export const EXPORT_FIELDS_TO_HIDE_FROM_MARKER_LABEL = [
    ExportField.AssetId,
    ExportField.TimestampCreated,
    ExportField.TimestampModified,
    ExportField.DatetimeCreated,
    ExportField.DatetimeModified,
    ExportField.Longitude,
    ExportField.Latitude,
    ExportField.CoordinatesLatLng,
    ExportField.LengthM,
    ExportField.LengthFt,
    ExportField.AreaSqM,
    ExportField.AreaSqFt,
    ExportField.PerimeterM,
    ExportField.PerimeterFt,
    ExportField.VolumeCuFt,
    ExportField.VolumeCuM,
    ExportField.DbhIn,
    ExportField.DbhCm,
];

export const ASSET_FIELD_WIDTH_OVERRIDES = {
    // Each asset type has an array of <attribute internal name>'s or <export field>'s
    // Entry 'Map': '0 0 55px' is added to all assets
    [AssetType.Tree]: {
        'tree_id': '0 0 85px',
        [ExportField.SpeciesNameBoth]: '1.5',
        [ExportField.Dbh]: '0 0 60px',
        'condition': '0 0 105px',
        'primary_maintenance': '0 0 210px',
        [ExportField.DatetimeModified]: '0 0 210px',
        'notes': '2',
    },
};

export const EXPORT_FIELDS_SHOWN_IN_ASSET_TABLE = {
    // Each asset type has an array of <attribute internal name>'s or <export field>'s
    [AssetType.Tree]: [
        'tree_id',
        'label',
        ExportField.SpeciesNameBoth,
        ExportField.Dbh,
        'condition',
        'primary_maintenance',
        ExportField.DatetimeModified,
        'notes'
    ],
    [AssetType.Any]: [
        'label',
        'notes',
    ],
    [AssetType.Line]: [
        'label',
        ExportField.Length,
        'notes',
    ],
    [AssetType.Polygon]: [
        'label',
        ExportField.Area,
        ExportField.Perimeter,
        'notes',
    ],
    [AssetType.Pavement]: [
        'label',
        'pavement_type',
        ExportField.Area,
        ExportField.Perimeter,
        'notes',
    ],
};

export const SPECIES_EXPORT_FIELDS = [
    ExportField.SpeciesNameBoth,
    ExportField.SpeciesNameScientific,
    ExportField.SpeciesNameCommon,
    ExportField.SpeciesType,
    ExportField.SpeciesLeafType,
    ExportField.SpeciesPercentLeafType,
    ExportField.SpeciesCode,
    ExportField.SpeciesGenusScientific,
    ExportField.SpeciesGenusCommon,
];

export const DEFAULT_USER_EMAIL = 'OfflineCollectorUser@davey.com';

export const MAP_STATUS = {
    // This obj must match the MapStatus class in ./backend/api/models/classes.py
    'NOT_REQUESTED': 'Not requested yet.',
    'REQUESTED': 'Map requested, not started yet.',
    'IN_PROGRESS': 'Working on map.',
    'COMPLETED': 'Completed.',
    'REJECTED': 'Unable to complete.',
};

export const SAMPLE_STATUS = {
    // This obj must match the SampleSubmissionStatus class in ./backend/api/models/classes.py
    'NOT_REQUESTED': 'Not submitted yet',
    'REQUESTED': 'Lab notified, not started yet',
    'RECEIVED': 'Received (physical sample)',
    'IN_PROGRESS': 'Processing',
    'COMPLETED': 'Diagnosed',
    'REJECTED': 'Unable to diagnose',
};

export const SHARED_USER_ATTRIBUTES = [
    {
        'category_name': '',
        'attribute_type': AttributeType.String,
        'name': 'Owner',
        'internal_name': 'owner',
        'required': false,
        'repeatable': false,
        'readOnly': true,
        'minLen': 0,
        'maxLen': 200,
        'placeholder': 'Firstname.Lastname@davey.com',
    },
    {
        'category_name': '',
        'attribute_type': AttributeType.String,
        'name': 'Viewers',
        'internal_name': 'viewers',
        'required': false,
        'repeatable': true,
        'minLen': 0,
        'maxLen': 200,
        'placeholder': 'Firstname.Lastname@davey.com',
    },
    {
        'category_name': '',
        'attribute_type': AttributeType.String,
        'name': 'Editors',
        'internal_name': 'editors',
        'required': false,
        'repeatable': true,
        'minLen': 0,
        'maxLen': 200,
        'placeholder': 'Firstname.Lastname@davey.com',
    },
];

export const HexColor = {
    'White': '#ffffff',
    'Black': '#000000',
    'Red': '#ff0000',
    'Green': '#00ff00',
    'Blue': '#0000ff',
    'DaveyGreen': '#0f693f',
    'DaveyGreenDark': '#0e3c29',
    'MapHighlightOrange': '#ff7707',
    'MapTrackingBlue': '#0783ff',
    'MapClusterBlue': '#6ec9f1',
    'LightBlue': '#aad1fb',
    'MediumGray': '#6d6d6d',
};

export const MapSymbol = {
    // Non-point symbols must end with "_line"
    'Circle': 'circle',
    'Square': 'square',
    'Diamond': 'diamond',
    'DiagonalCross': 'diagonal_cross',
    'Cross': 'cross',
    'Triangle': 'triangle',
    'InvertedTriangle': 'inverted_triangle',
    'SevenPtStar': 'seven_pt_star',
    'SixPtStar': 'six_pt_star',
    'FivePtStar': 'five_pt_star',
    'FourPtStar': 'four_pt_star',
    'ThreePtStar': 'three_pt_star',

    'SolidLine': 'solid_line',
    'DashedLine': 'dashed_line',
    'DottedLine': 'dotted_line',
};

export const FEATURE_TYPE_MAP_SYMBOLS = {
    [FeatureType.Point]: [
        MapSymbol.Circle,
        MapSymbol.Square,
        MapSymbol.Diamond,
        MapSymbol.DiagonalCross,
        MapSymbol.Cross,
        MapSymbol.Triangle,
        MapSymbol.InvertedTriangle,
        MapSymbol.SevenPtStar,
        MapSymbol.SixPtStar,
        MapSymbol.FivePtStar,
        MapSymbol.FourPtStar,
        MapSymbol.ThreePtStar,
    ],
    [FeatureType.Line]: [
        MapSymbol.SolidLine,
        MapSymbol.DashedLine,
        MapSymbol.DottedLine,
    ],
    [FeatureType.Polygon]: [
        MapSymbol.SolidLine,
        MapSymbol.DashedLine,
        MapSymbol.DottedLine,
    ],
    [FeatureType.Circle]: [
        MapSymbol.SolidLine,
        MapSymbol.DashedLine,
        MapSymbol.DottedLine,
    ],
};


export const MAP_SYMBOL_RADIUS_COMPENSATION = {
    [MapSymbol.Square]: 1.10,
    [MapSymbol.Diamond]: 1.10,
    [MapSymbol.DiagonalCross]: 1.30,
    [MapSymbol.Cross]: 1.20,
    [MapSymbol.Triangle]: 1.20,
    [MapSymbol.InvertedTriangle]: 1.20,
    [MapSymbol.SevenPtStar]: 1.60,
    [MapSymbol.SixPtStar]: 1.50,
    [MapSymbol.FivePtStar]: 1.50,
    [MapSymbol.FourPtStar]: 1.50,
    [MapSymbol.ThreePtStar]: 1.60,
};

export const MapControl = {
    'ZoomIn': 'zoom_in',
    'ZoomOut': 'zoom_out',
    'RotateNorth': 'rotate_north',
    'ToggleLegend': 'toggle_legend',
    'LocateMe': 'locate_me',
    'Save': 'save',
    'SearchAddress': 'search_address',
    'CenterOnFeatures': 'center_on_features',
    'CenterOnFeaturesExternal': 'center_on_features_external',
    'GenerateMap': 'generate_map',
    'MapSettings': 'map_settings',
    'ManageData': 'manage_data',
    'ProjectList': 'project_list',
    'CreateLink': 'create_link',
    'LockMovement': 'lock_movement',
    'HideControls': 'hide_controls',
    'SizeTotals': 'size_totals',
    'SizeTotalsOnScreen': 'size_totals_on_screen',
    'SaveEphemeral': 'save_ephemeral',
    'DownloadGeojson': 'download_geojson',
    'DownloadShp': 'download_shp',
    'DownloadKml': 'download_kml',
    'SizeTotalsAreaEstimator': 'size_totals_area_estimator',
    'SizeTotalsOnScreenAreaEstimator': 'size_totals_on_screen_area_estimator',
    'DeleteBoundariesAreaEstimator': 'delete_boundaries_area_estimator',
};

export const FRACTION_SYMBOLS = {
    '1/2': '½',
    '1/3': '⅓',
    '2/3': '⅔',
    '1/4': '¼',
    '3/4': '¾',
    '1/5': '⅕',
    '2/5': '⅖',
    '3/5': '⅗',
    '4/5': '⅘',
    '1/6': '⅙',
    '5/6': '⅚',
    '1/7': '⅐',
    '1/8': '⅛',
    '3/8': '⅜',
    '5/8': '⅝',
    '7/8': '⅞',
    '1/9': '⅑',
    '1/10': '⅒',
    '1/16': '¹⁄₁₆',
    '3/16': '³⁄₁₆',
    '5/16': '⁵⁄₁₆',
    '7/16': '⁷⁄₁₆',
    '9/16': '⁹⁄₁₆',
    '11/16': '¹¹⁄₁₆',
    '13/16': '¹³⁄₁₆',
    '15/16': '¹⁵⁄₁₆'
};

export const LAB_SAMPLES_SKIPPED_EXPORT_FIELDS = {
    [AssetType.PlantSample]: [
        ExportField.TimestampCreated,
        ExportField.TimestampModified,
        'photo',
        ExportField.Longitude,
        ExportField.Latitude,
        ExportField.CoordinatesLngLat,
        'asset_type',
    ],
    [AssetType.TurfSample]: [
        ExportField.TimestampCreated,
        ExportField.TimestampModified,
        'photo',
        ExportField.Longitude,
        ExportField.Latitude,
        ExportField.CoordinatesLngLat,
        'asset_type',
    ],
};

export const AREA_ESTIMATOR_TYPES = {
    // This must match the array in backend/lib/attributes/definitions.py
    'AllTypes': [
        'Boundary',
        'Soft Edging',
        'Hard Edging',
        'Sidewalk',
        'Driveway',
        'Roads',

        'Beds',
        'Turf',
        'Turf Low Maint',
        'Turf Other',
        'Parking',
        'Common Area',
        'Other Hard Surface',
    ],
    'LineTypes': [
        'Boundary',
        'Soft Edging',
        'Hard Edging',
        'Sidewalk',
        'Driveway',
        'Roads',
    ],
    'PolygonTypes': [
        'Beds',
        'Turf',
        'Turf Low Maint',
        'Turf Other',
        'Parking',
        'Common Area',
        'Other Hard Surface',
    ],
};